import { motion } from 'framer-motion';
import { Calendar, MapPin, Plus } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOverlay } from '../../contexts/OverlayContext';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { typography } from '../../styles/typography';
import { Card, SuccessOverlay } from '../ui';
import { productTemplateApi, productionTemplateApi } from './templateApi';

const getCurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const ProductTemplateView = () => {
  // Log screen view
  useScreenLogging('ProductTemplateView', {
    component: 'ProductTemplateView',
    screenType: 'product-template',
  });

  const navigate = useNavigate();
  const { openOverlay } = useOverlay();
  const [selectedSite, setSelectedSite] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [sites, setSites] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [isLoadingSites, setIsLoadingSites] = useState(true);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [error, setError] = useState('');
  const [lastKey, setLastKey] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [templatesRefreshKey, setTemplatesRefreshKey] = useState(0);
  const [showDeleteSuccessOverlay, setShowDeleteSuccessOverlay] = useState(false);


  // Fetch sites on mount
  useEffect(() => {
    const fetchSites = async () => {
      setIsLoadingSites(true);
      try {
        const sitesData = await productTemplateApi.getSites();
        const sitesList = Array.isArray(sitesData) 
          ? sitesData 
          : sitesData?.sites || sitesData?.data || [];
        
        // Sort sites alphabetically by name
        const sortedSites = [...sitesList].sort((a, b) => {
          const nameA = (a.site_name || a.name || 'Unknown Site').toLowerCase();
          const nameB = (b.site_name || b.name || 'Unknown Site').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        
        setSites(sortedSites);
        // Don't auto-select - let user select from dropdown
      } catch (error) {
        console.error('Error fetching sites:', error);
        setError('Failed to load sites. Please refresh the page.');
      } finally {
        setIsLoadingSites(false);
      }
    };
    fetchSites();
  }, []);

  // Fetch templates when site or month changes
  useEffect(() => {
    const fetchTemplates = async () => {
      if (!selectedSite) {
        setTemplates([]);
        return;
      }

      setIsLoadingTemplates(true);
      setError('');
      
      try {
        // Calculate date range for the selected month
        const [year, month] = selectedMonth.split('-');
        const startDate = `${year}-${month}-01`;
        const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
        const endDate = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;

        const result = await productTemplateApi.listTemplates({
          siteId: selectedSite.site_id || selectedSite.id,
          status: 'active',
          limit: 100,
        });

        if (result.success) {
          // Filter by month based on created_at
          const filtered = (result.templates || []).filter((template) => {
            if (!template.created_at) return false;
            const templateDate = new Date(template.created_at);
            const templateYear = templateDate.getFullYear();
            const templateMonth = templateDate.getMonth() + 1;
            return templateYear === parseInt(year) && templateMonth === parseInt(month);
          });

          setTemplates(filtered);
          setLastKey(result.lastKey);
          setHasMore(result.lastKey !== null);
        } else {
          setError(result.message || 'Failed to load templates');
          setTemplates([]);
        }
      } catch (error) {
        console.error('Error fetching templates:', error);
        setError('Failed to load templates. Please try again.');
        setTemplates([]);
      } finally {
        setIsLoadingTemplates(false);
      }
    };

    fetchTemplates();
  }, [selectedSite, selectedMonth, templatesRefreshKey]);

  const handleDownload = (template) => {
    const createdDate = template.created_at 
      ? new Date(template.created_at).toLocaleDateString()
      : 'N/A';
    
    const csvData = [
      ['Product Name', 'Category', 'SKU', 'Price', 'Cost', 'Unit', 'Barcode', 'Description', 'Site', 'Created At'],
      [
        template.template_name || template.product_name || template.productName || 'N/A',
        template.category || 'N/A',
        template.sku || 'N/A',
        template.price || '0',
        template.cost || '0',
        template.unit || 'N/A',
        template.barcode || 'N/A',
        template.description || 'N/A',
        template.site_name || 'N/A',
        createdDate,
      ],
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `product-template-${template.template_name || template.product_name || template.productName || 'template'}-${createdDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };


  const handleOpenDetails = async (template) => {
    console.log('🖼️ Product Template View: Card clicked, template data:', {
      template_name: template.template_name,
      photo_key: template.photo_key,
      production_template_id: template.production_template_id,
      allKeys: Object.keys(template),
      fullTemplate: template,
    });
    
    // Fetch full template details to get photo_key (list API doesn't return it)
    // Use production templates endpoint (same backend, CORS configured)
    const templateId = template.production_template_id || template.product_template_id || template.id;
    let templateToShow = template;
    
    if (templateId && !template.photo_key) {
      console.log('🖼️ Product Template View: Fetching full template details for photo_key...');
      try {
        // Use production template API (same backend, has CORS configured)
        const result = await productionTemplateApi.getTemplate(templateId);
        if (result.success && result.template) {
          console.log('✅ Product Template View: Got full template details:', {
            photo_key: result.template.photo_key,
            photoKey: result.template.photoKey,
            allKeys: Object.keys(result.template),
            fullTemplate: result.template,
          });
          // Merge the full template data with the existing template
          templateToShow = { ...template, ...result.template };
        } else {
          console.warn('⚠️ Product Template View: Failed to fetch template details, using list data');
        }
      } catch (error) {
        console.error('❌ Product Template View: Error fetching template details:', error);
      }
    }
    
    // Open overlay with template data
    openOverlay('productTemplateDetails', {
      template: templateToShow,
      onClose: () => {
        console.log('Product Template View: Modal closed');
      },
      onDeleted: () => {
        setTemplatesRefreshKey((prev) => prev + 1);
        setShowDeleteSuccessOverlay(true);
      },
    });
  };

  return (
    <div className="w-full min-h-screen">
      <div className="mx-auto h-auto">
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          className="space-y-8"
        >
          {/* Header with Title and Create Button */}
          <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className={typography.h2} style={{ color: '#000000' }}>
                Product Templates
              </h1>
              <p className="mt-2 text-gray-600">
                View and manage product templates
              </p>
            </div>
            <button
              onClick={() => navigate('/onboarding-product-template/create')}
              className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-green-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
            >
              <Plus className="w-4 h-4" />
              Create Product Template
            </button>
          </div>

          <div className="rounded-3xl">
            {/* Site and Month Selection */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
              <div className="min-w-[200px]">
                <label htmlFor="product-template-site" className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
                  <MapPin className="w-4 h-4" />
                  Select Site
                </label>
                <select
                  id="product-template-site"
                  value={selectedSite ? (selectedSite.site_id || selectedSite.id) : ''}
                  onChange={(e) => {
                    const siteId = e.target.value;
                    const site = sites.find(s => (s.site_id || s.id) === siteId);
                    setSelectedSite(site || null);
                  }}
                  className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                  required
                >
                  <option value="">
                    {isLoadingSites ? 'Loading sites...' : 'Select a site'}
                  </option>
                  {/* alphabetical order */}
                  {Array.isArray(sites) && sites.map((site) => (
                    <option key={site.site_id || site.id} value={site.site_id || site.id}>
                      {site.site_name || site.name || 'Unknown Site'}
                    </option>
                  ))}
                </select>
              </div>
              <div className="min-w-[200px]">
                <label htmlFor="product-template-month" className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
                  <Calendar className="w-4 h-4" />
                  Select Month
                </label>
                <input
                  id="product-template-month"
                  type="month"
                  value={selectedMonth}
                  onChange={(event) => setSelectedMonth(event.target.value)}
                  className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                />
              </div>
            </div>

            {/* Product Template Cards */}
            <div className="space-y-4">
              {error && (
                <Card.Light className="p-4 bg-red-50 border border-red-200">
                  <p className="text-sm text-red-800">{error}</p>
                </Card.Light>
              )}
              {isLoadingTemplates ? (
                <Card.Light className="p-8 text-center">
                  <p className="text-gray-500 text-lg">Loading templates...</p>
                </Card.Light>
              ) : templates.length > 0 ? (
                templates.map((template, index) => (
                    <motion.div
                      key={template.product_template_id || template.id || index}
                      variants={cardVariants}
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                      onClick={() => handleOpenDetails(template)}
                      className="cursor-pointer"
                    >
                      <Card.Light
                        className="p-6 cursor-pointer hover:shadow-lg transition-shadow"
                        onClick={() => handleOpenDetails(template)}
                      >
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3 mb-2">
                              {/* <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold">
                                {index + 1}
                              </div> */}
                              <h3 className="text-xl font-semibold text-gray-800">
                                {template.template_name || template.product_name || template.productName || 'Unnamed Product'}
                              </h3>
                              {template.category && (
                                <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 self-start md:self-auto">
                                  {template.category}
                                </span>
                              )}
                              {template.created_at && (
                                <div className="text-sm md:text-base">
                                  <span className="text-gray-600">Created: </span>
                                  <span className="font-medium">
                                    {new Date(template.created_at).toLocaleDateString()}
                                  </span>
                                </div>
                              )}
                            </div>
                            {template.description && (
                              <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                                {template.description}
                              </p>
                            )}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                              {template.sku && (
                                <div>
                                  <span className="text-gray-600">SKU: </span>
                                  <span className="font-medium">{template.sku}</span>
                                </div>
                              )}
                              {template.price !== undefined && (
                                <div>
                                  <span className="text-gray-600">Price: </span>
                                  <span className="font-medium">₹{template.price}</span>
                                </div>
                              )}
                              {template.cost !== undefined && (
                                <div>
                                  <span className="text-gray-600">Cost: </span>
                                  <span className="font-medium">₹{template.cost}</span>
                                </div>
                              )}
                              {template.unit && (
                                <div>
                                  <span className="text-gray-600">Unit: </span>
                                  <span className="font-medium">{template.unit}</span>
                                </div>
                              )}
                              {/* {template.site_name && (
                                <div>
                                  <span className="text-gray-600">Site: </span>
                                  <span className="font-medium">{template.site_name}</span>
                                </div>
                              )} */}
                            </div>
                          </div>
                        </div>

                        {/* <div className="flex gap-2 mt-4">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(template);
                            }}
                            className="w-10 h-10 p-2 bg-blue-700 rounded flex justify-center items-center gap-2.5 cursor-pointer hover:bg-blue-800 transition-colors"
                            title="Download"
                          >
                            <Download className="w-6 h-6 text-white stroke-white" strokeWidth={2} />
                          </button>
                        </div> */}
                      </Card.Light>
                    </motion.div>
                  ))
              ) : (
                <Card.Light className="p-8 text-center">
                  <p className="text-gray-500 text-lg">
                    {!selectedSite
                      ? 'Please select a site to view product templates.'
                      : 'No product templates found for the selected site and month.'}
                  </p>
                </Card.Light>
              )}
            </div>

          </div>
        </motion.div>
      </div>

      <SuccessOverlay
        open={showDeleteSuccessOverlay}
        onClose={() => setShowDeleteSuccessOverlay(false)}
        title="Product Template Deleted Successfully"
        message="The product template has been deleted and the list is refreshed."
        buttonLabel="Done"
      />
    </div>
  );
};

export default ProductTemplateView;

